import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Form, Input, Select, Button, Segmented, Radio } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import ModelWrapper from "../../../modal/ModelWrapper";
import { CustomButton, CustomSecondaryButton } from "../../physician_page/styled";
import { notify } from "../../../../utils/helper";
import styled from "styled-components";
import { extractToken } from "../../../../utils/localStorage";
import videoService from "../../../../services/videos";
import journeyService from "../../../../services/journey";
import { SpecilityContext } from "../../../../context/SpecilityContext";
import { globalRoles } from "../../../../utils/roles";

const { Option } = Select;
const { TextArea } = Input;

const ModalContent = styled.div`
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
  margin-bottom: 12px;
  margin-top: 24px;

  &:first-child {
    margin-top: 0;
  }
`;

const VideoStepContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: var(--secondary-bg);
  position: relative;
`;

const StepNumber = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #8a3ef4;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const StepContent = styled.div`
  margin-left: 48px;
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 40px !important;
    border-radius: 6px !important;
    background-color: var(--primary-bg) !important;
    border: 1px solid var(--border-color) !important;
  }

  .ant-select-selection-item {
    line-height: 0px !important;
    color: var(--primary-text-color);
    overflow: visible;
  }

  &:hover .ant-select-selector {
    border-color: #8a3ef4 !important;
  }
`;

const AddStepButton = styled(Button)`
  width: 100%;
  height: 48px;
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  background-color: var(--primary-bg);
  color: var(--primary-text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  margin-top: 8px;

  &:hover {
    border-color: #8a3ef4;
    color: #8a3ef4;
    background-color: var(--secondary-bg);
  }
`;

const DeleteButton = styled(Button)`
  color: #ff4d4f;
  border: none;
  background: transparent;
  padding: 4px 8px;

  &:hover {
    background-color: rgba(255, 77, 79, 0.1);
    color: #ff4d4f;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
`;

const CreateTemplateModal = ({ isOpen, setOpen, onSuccess, templateData, isEdit, setIsEdit }) => {
  const [form] = Form.useForm();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [videoSteps, setVideoSteps] = useState([{ step: 1, videoId: null }]);
  const { specialtyOptions } = useContext(SpecilityContext);
  const user = extractToken();
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;
  const isLibraryManager = user?.role === globalRoles.LIBRARY_MANAGER;
  const isPhysician = user?.role === globalRoles.PHYSICIAN || user?.role === globalRoles.ASSISTANT;
  const isCoordinator = user?.role === globalRoles.CCM_COORDINATOR || user?.role === globalRoles.CCM_MANAGER;


  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await videoService.getVideos(100, 1, "", "", "", "", "", "");
      if (response?.data?.videos?.results) {
        // Only include videos with videoType === "ccm"
        const filteredVideos = response.data.videos.results.filter(
          (video) => video.videoType === "ccm"
        );
        setVideos(filteredVideos);
      } else {
        setVideos([]);
      }
    } catch (error) {
      notify("error", "Failed to load videos");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isEdit && templateData) {
        form.setFieldsValue({
          title: templateData.name,
          description: templateData.description,
          specialty: templateData.specialty,
          // condition: templateData.condition,
          type: templateData.type,
          templateType: templateData.templateType,
        });

        if (templateData.steps && templateData.steps.length > 0) {
          const steps = templateData.steps.map((step, index) => ({
            step: index + 1,
            videoId: step.video?.id || step.videoId || null,
            title: step.video?.title || "",
          }));
          setVideoSteps(steps);

          const formValues = {};
          templateData.steps.forEach((s, index) => {
            formValues[`videoStep_${index}`] = s.video?.id || s.videoId;
          });
          form.setFieldsValue(formValues);
        }
      } else {
        form.resetFields();
        form.setFieldsValue({ type: "draft" });
        setVideoSteps([{ step: 1, videoId: null }]);
      }
      fetchVideos();
    }
  }, [isOpen, isEdit, templateData, form]);

  const handleAddStep = () => {
    const nextStep = videoSteps.length + 1;
    setVideoSteps([...videoSteps, { step: nextStep, videoId: null }]);
  };

  const handleRemoveStep = (index) => {
    if (videoSteps.length > 1) {
      const newSteps = videoSteps.filter((_, i) => i !== index);
      // Re-number steps
      const renumberedSteps = newSteps.map((step, i) => ({
        ...step,
        step: i + 1,
      }));
      setVideoSteps(renumberedSteps);
      form.setFieldsValue({
        videoSteps: renumberedSteps.map((s) => s.videoId),
      });
    }
  };

  const handleVideoChange = (stepIndex, videoId) => {
    const newSteps = [...videoSteps];
    newSteps[stepIndex].videoId = videoId;
    setVideoSteps(newSteps);
    form.setFieldsValue({
      videoSteps: newSteps.map((s) => s.videoId),
    });
  };

  const handleSubmit = async (values) => {
    if (videoSteps.some((step) => !step.videoId)) {
      notify("warning", "Please select a video for all steps");
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare steps array
      const steps = videoSteps.map((step, index) => ({
        videoId: step.videoId,
        title: "",
        order: index,
      }));

      const payload = {
        specialty: isCoordinator ? "CCM - Chronic Care Management" : values.specialty,
        // condition: values.condition || "",
        name: values.title,
        description: values.description || "",
        type: values.type || "",
        steps: steps,
        templateType: isPhysician ? "clinical" : isCoordinator ? "ccm" : values.templateType || "",
      };

      if (isEdit && templateData?.id) {
        await journeyService.updateTemplate(templateData.id, payload);
        notify("success", "Template updated successfully");
      } else {
        await journeyService.createTemplate(payload);
        notify("success", "Template created successfully");
      }

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      notify("error", isEdit ? "Failed to update template" : "Failed to create template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setVideoSteps([{ step: 1, videoId: null }]);
    setOpen(false);
    setIsEdit(false);
  };

  // Get selected video IDs to filter them out from other steps
  const getSelectedVideoIds = (currentIndex) => {
    return videoSteps
      .map((step, index) => (index !== currentIndex ? step.videoId : null))
      .filter((id) => id !== null);
  };


  return (
    <ModelWrapper
      show={isOpen}
      setShow={handleClose}
      title={
        <div className="row-flex flex-column">
          <div className="font-18 font-semibold text-primary">
            {isEdit ? "Edit Template" : "Create New Template"}
          </div>
        </div>
      }
      footer={null}
      width={700}
      keyClose={false}
      onCancel={handleClose}
      className="custom-detail-modal"
    >
      <ModalContent>
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
        >
          <SectionTitle>Template Details</SectionTitle>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter template title!" }]}
          >
            <Input
              placeholder="Enter template title"
              size="large"
              style={{
                borderRadius: "6px",
                backgroundColor: "var(--primary-bg)",
                border: "1px solid var(--border-color)",
              }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Please enter template description!" },
            ]}
          >
            <TextArea
              placeholder="Enter template description"
              rows={4}
              style={{
                borderRadius: "6px",
                backgroundColor: "var(--primary-bg)",
                border: "1px solid var(--border-color)",
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            {!isCoordinator && (
            <Col span={24}>
              <Form.Item
                name="specialty"
                label="Medical Specialty"
                rules={[{ required: true, message: "Please select a specialty!" }]}
              >
                <StyledSelect
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  placeholder="Select medical specialty"
                  size="large"
                  showSearch
                  options={specialtyOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            )}

            {/* <Col span={12}>
              <Form.Item
                name="condition"
                label="Condition"
                rules={[{ required: true, message: "Please enter condition!" }]}
              >
                <Input
                  placeholder="Enter condition"
                  size="large"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--primary-bg)",
                    border: "1px solid var(--border-color)",
                  }}
                />
              </Form.Item>
            </Col> */}
          </Row>

         <Row gutter={16}>
         <Col span={12}>
          <Form.Item
            name="type"
            label="Template Status"
            rules={[{ required: true, message: "Please select template type!" }]}
          >
            <Radio.Group
              value={form.getFieldValue("type")}
              onChange={(e) => form.setFieldValue("type", e.target.value)}
            >
              <Radio value="draft">Draft</Radio>
              <Radio value="public">Public</Radio>
              <Radio value="private">Private</Radio>
            </Radio.Group>
          </Form.Item>
          </Col>

          {(isSuperAdmin || isLibraryManager) && (
          <Col span={12}>
            <Form.Item
              name="templateType"
              label="Template Type"
              rules={[{ required: true, message: "Please select template type!" }]}
            >
              <Radio.Group
                value={form.getFieldValue("templateType")}
                onChange={(e) => form.setFieldValue("templateType", e.target.value)}
              >
                <Radio value="clinical">Clinical</Radio>
                <Radio value="ccm">CCM</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          )}
          </Row>



          <SectionTitle>Video Steps</SectionTitle>

          {videoSteps.map((step, index) => {
            const selectedVideoIds = getSelectedVideoIds(index);
            let availableVideos = videos.filter(
              (video) => !selectedVideoIds.includes(video.id)
            );

            // If current step has a video that's not in availableVideos, add it
            if (step.videoId) {
              const currentVideo = videos.find((v) => v.id === step.videoId);
              if (!currentVideo) {
                const placeholderVideo = {
                  id: step.videoId,
                  title: step.title || "Video (Not Found)",
                };
                availableVideos = [placeholderVideo, ...availableVideos];
              } else if (selectedVideoIds.includes(step.videoId)) {
                availableVideos = [currentVideo, ...availableVideos];
              }
            }

            return (
              <VideoStepContainer key={index}>
                <StepNumber>{step.step}</StepNumber>
                <StepContent>
                  <StepHeader>
                    <StepTitle>Step {step.step} - Select Video</StepTitle>
                    {videoSteps.length > 1 && (
                      <DeleteButton
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveStep(index)}
                        type="text"
                      />
                    )}
                  </StepHeader>
                  <Form.Item
                    name={`videoStep_${index}`}
                    rules={[
                      {
                        required: true,
                        message: "Please select a video for this step!",
                      },
                    ]}
                  >
                    <StyledSelect
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      placeholder="Select a video"
                      size="large"
                      value={step.videoId}
                      onChange={(value) => handleVideoChange(index, value)}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      loading={loading}
                    >
                      {availableVideos.map((video) => (
                        <Option
                          key={video.id}
                          value={video.id}
                          label={video.title || "Untitled Video"}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <PlayCircleOutlined />
                            <span>{video.title || "Untitled Video"}</span>
                          </div>
                        </Option>
                      ))}
                    </StyledSelect>
                  </Form.Item>
                </StepContent>
              </VideoStepContainer>
            );
          })}

          <AddStepButton
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddStep}
          >
            Add Next Step
          </AddStepButton>

          <ButtonContainer>
            <CustomSecondaryButton
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </CustomSecondaryButton>
            <CustomButton htmlType="submit" loading={submitting}>
              {submitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Template" : "Create Template")}
            </CustomButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModelWrapper>
  );
};

export default CreateTemplateModal;

